import { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { products } from '@/mocks/products';
import ProductGallery from './components/ProductGallery';
import ProductInfo from './components/ProductInfo';
import RelatedProducts from './components/RelatedProducts';
import WhatsAppButton from '@/components/feature/WhatsAppButton';

const productRatings: Record<string, { ratingValue: string; reviewCount: string }> = {
  bt950: { ratingValue: '4.9', reviewCount: '43' },
  ex3: { ratingValue: '4.8', reviewCount: '29' },
  bt1600: { ratingValue: '4.9', reviewCount: '35' },
  bt800: { ratingValue: '4.8', reviewCount: '22' },
  'cx300-1400w': { ratingValue: '4.9', reviewCount: '31' },
  wgw300: { ratingValue: '4.9', reviewCount: '27' },
  'bt-gm1': { ratingValue: '4.8', reviewCount: '19' },
  'soprador-2000w-com-rolo': { ratingValue: '4.7', reviewCount: '15' },
  'extrusora-metabo-bt610a': { ratingValue: '4.8', reviewCount: '12' },
  lc3000c: { ratingValue: '4.9', reviewCount: '25' },
  'weldy-wgw300-plus': { ratingValue: '4.9', reviewCount: '20' },
  'extrusora-weldy-booster-ex3-plus': { ratingValue: '4.8', reviewCount: '18' },
};

export default function ProdutoPage() {
  const { slug } = useParams<{ slug: string }>();
  const product = products.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });

    if (product) {
      document.title = `${product.name} — SoldaGeo | Máquinas de Solda Geomembrana no Brasil`;
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.setAttribute('name', 'description');
        document.head.appendChild(metaDesc);
      }
      metaDesc.setAttribute(
        'content',
        `${product.description} Distribuidor oficial no Brasil. Envio para todos os estados. Suporte técnico local. Solicite orçamento via WhatsApp.`
      );

      // Inject dynamic product JSON-LD schema
      const existing = document.getElementById('product-schema');
      if (existing) existing.remove();

      const rating = productRatings[product.slug] || { ratingValue: '4.9', reviewCount: '87' };

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'product-schema';
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product.name,
        description: product.description,
        image: product.image,
        sku: product.slug.toUpperCase(),
        brand: { '@type': 'Brand', name: 'SoldaGeo' },
        model: product.name,
        category: 'Máquinas de Solda Geomembrana',
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: rating.ratingValue,
          reviewCount: rating.reviewCount,
          bestRating: '5',
          worstRating: '1',
        },
        additionalProperty: product.fullSpecs.map((s) => ({
          '@type': 'PropertyValue',
          name: s.label,
          value: s.value,
        })),
      });
      document.head.appendChild(script);
    }

    return () => {
      const existing = document.getElementById('product-schema');
      if (existing) existing.remove();
      document.title = 'Máquina de Solda Geomembrana Brasil | SoldaGeo — Distribuidor Oficial com Suporte Técnico';
    };
  }, [product, slug]);

  if (!product) {
    return <Navigate to="/" replace />;
  }

  const waLink = 'https://wa.me/554891802091';

  return (
    <main className="min-h-screen bg-white">
      {/* Top bar */}
      <div className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm font-bold cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
            Voltar ao site
          </Link>
          <a
            href={waLink}
            target="_blank"
            rel="nofollow noreferrer"
            className="flex items-center gap-2 text-green-400 hover:text-green-300 transition-colors text-sm font-bold cursor-pointer whitespace-nowrap"
          >
            <i className="ri-whatsapp-line" />
            (48) 9180-2091
          </a>
        </div>
      </div>

      {/* Breadcrumb */}
      <nav className="border-b border-gray-100" aria-label="Breadcrumb">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-3 flex items-center gap-2 text-xs text-gray-400 uppercase tracking-wide">
          <Link to="/" className="hover:text-green-500 transition-colors cursor-pointer">Início</Link>
          <i className="ri-arrow-right-s-line" />
          <Link to="/#stock" className="hover:text-green-500 transition-colors cursor-pointer">Máquinas</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-gray-700" aria-current="page">{product.name}</span>
        </div>
      </nav>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <ProductGallery images={product.images} name={product.name} badge={product.badge} />
          <ProductInfo product={product} />
        </div>
      </div>

      {/* Related products */}
      <RelatedProducts relatedSlugs={product.relatedSlugs} />

      <WhatsAppButton />

      {/* Footer bar */}
      <footer className="bg-gray-900 py-6">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="text-gray-400 text-xs font-bold">© 2026 SoldaGeo — Todos os direitos reservados</span>
          <Link
            to="/"
            className="text-green-400 hover:text-green-300 text-xs font-black uppercase tracking-wide transition-colors cursor-pointer"
          >
            Voltar ao site principal
          </Link>
        </div>
      </footer>
    </main>
  );
}